import React, { useRef, useState, useEffect } from 'react';
import { jsonEditorStyles } from '../jsonEditorStyles';
import FormControl from '@mui/material/FormControl';
import { Box, Typography, Checkbox, FormControlLabel, SelectChangeEvent, TextField, FormHelperText } from '@mui/material';
import { TextComponent, SelectComponent } from '../../uiComponents';

import { useSelector, useDispatch } from 'react-redux';
import { getTemplatizationFields } from '../../../store';
import { OptionProps, TextPropsData } from '../../../view-models';
import { NavigationType } from 'react-router';
import { AnyTxtRecord } from 'dns';
import { NormalCssProperties } from '@mui/material/styles/createMixins';

export interface TemplatizedValue {
    value: string,
    templatized: boolean
}

export interface TemplatizedDataProps {
    style?: NormalCssProperties;
    id?: string;
    disabled?: boolean;
    label: string;
    error?: boolean;
    helperText?: string;
    required?: boolean;
    fieldPath: string;
    value: string;
    variant: "text" | "select";
    manualOptions?: OptionProps[];
}

export interface TextComponentProps {
    modifiedTemplatizations: any;
    setModifiedTemplatizations: any;
    data: TemplatizedDataProps;
    changeHandler: (e: TemplatizedValue) => void;
}

// Safely fetch a possibly-templatized value.
// For backwards-compatability reasons, this needs to treat the value as potentially not a TemplatizedValue.
// It is assumed that templatizable fields have a string (or at least non-object) value before being templatized.
export function getTemplatizedValue(v: any) {
    return (typeof v === 'object') ? v.value : v;
}

function TemplatizedFieldComponent({ modifiedTemplatizations, setModifiedTemplatizations, data, changeHandler }: TextComponentProps) {
    const templatizationFields = useSelector(getTemplatizationFields);
    const [enteredText, setEnteredText] = useState<string>(modifiedTemplatizations[data.fieldPath] ? "" : (data.value ? data.value : ""));
    const [error, setError] = useState<string>('');
    const [selectedSetting, setSelectedSetting] = useState<string>(modifiedTemplatizations[data.fieldPath] ? modifiedTemplatizations[data.fieldPath] : "");
    const [templatize, setTemplatize] = useState<boolean>(!!modifiedTemplatizations[data.fieldPath]);

    const [fieldList, setFieldList] = useState<OptionProps[]>(Object.keys(templatizationFields).map((fKey) => { return { value: fKey, label: `From {settings:${templatizationFields[fKey].name}}`}}));

    const ref = useRef<null | ReturnType<typeof setTimeout>>(null);
    const style = data.style ? data.style : { margin: 1, width: '90%'}

    useEffect(() => {
        setFieldList(Object.keys(templatizationFields).map((fKey) => { return { value: fKey, label: `From {settings:${templatizationFields[fKey].name}}` } }));
    }, [templatizationFields])

    useEffect(() => {
        !!data.required && (((!templatize) && (!enteredText)) || (templatize && (!selectedSetting))) ? setError('Should not be empty') : setError('');
      }, [data]);

    const classes = jsonEditorStyles();
    return (
        <FormControl sx={style} required={data.required}>
            <div className={classes.flexRow}>
                {(!templatize) && (data.variant === 'text' ? (
                    <TextField
                        error={data?.error || !!error}
                        label={data.label}
                        variant="outlined"
                        size="small"
                        value={enteredText ? enteredText : ""}
                        type='textbox'
                        disabled={data?.disabled || (!!modifiedTemplatizations[data.fieldPath])}
                        onChange={(e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
                            setEnteredText(e.target.value);
                            if (ref.current) clearTimeout(ref.current);
                            ref.current = setTimeout(() => {
                                changeHandler({ value: e.target.value, templatized: false });
                            }, 500);
                        }}
                        sx={{mt: 1, width: '90%'}}
                    />)
                    : (
                        <SelectComponent
                            data={{
                                label: data.label,
                                value: enteredText ? enteredText : "",
                                options: data.manualOptions ?? [],
                                style: {marginTop: 1, width: '90%'}
                            }}
                            changeHandler={(e: SelectChangeEvent) => {
                                setEnteredText(e.target.value);
                                changeHandler({ value: e.target.value, templatized: false });
                            }}
                        />
                    )
                )}
                {templatize && (
                    <SelectComponent
                        data={{
                            label: data.label,
                            value: selectedSetting ? selectedSetting : "",
                            options: fieldList,
                            style: {marginTop: 1, width: '90%'}
                        }}
                        changeHandler={(e: SelectChangeEvent) => {
                            setSelectedSetting(e.target.value);
                            setModifiedTemplatizations({
                                ...modifiedTemplatizations,
                                [data.fieldPath]: e.target.value
                            });
                            changeHandler({ value: e.target.value, templatized: true });
                        }}
                    />
                )}
                <FormControlLabel
                    sx={{ mt: 1, ml: 1, flex: '0 0 auto' }}
                    label='Use value from app settings'
                    control={
                        <Checkbox
                            checked={templatize}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                if (!templatize) {
                                    if (!selectedSetting) {
                                        setSelectedSetting(fieldList[0].value);
                                    }
                                    setModifiedTemplatizations({
                                        ...modifiedTemplatizations,
                                        [data.fieldPath]: selectedSetting ? selectedSetting : fieldList[0].value
                                    });
                                    changeHandler({ value: selectedSetting ? selectedSetting : fieldList[0].value, templatized: true });
                                } else {
                                    setModifiedTemplatizations({
                                        ...modifiedTemplatizations,
                                        [data.fieldPath]: null
                                    });
                                    changeHandler({ value: enteredText.toString(), templatized: false });
                                }
                                setTemplatize(!templatize);
                            }}
                        />
                    }
                />
            </div>
            <FormHelperText sx={{ color: 'red' }}>{data.helperText || error}</FormHelperText>
        </FormControl>
    )
}

export default TemplatizedFieldComponent;